
import { debounce } from "lodash";
import { Options, prop, Vue } from "vue-class-component";
import { BookingV4Controller } from "@/app/ui/controllers/BookingV4Controller";
import { CommodityController } from "@/app/ui/controllers/CommodityController";
import SelectSearch from "@/app/ui/components/select-search/index.vue";
import { RequestListCommodityV4 } from "@/data/payload/api/BookingV4Request";
import {
  CommodityBookingV4,
  CommodityCategoryV4,
  CommodityCiplBookingV4,
  ErrorBookingV4
} from "@/domain/entities/BookingV4";
import { Commodity, CommodityCategoryData } from "@/domain/entities/Commodity";
import {
  formatInputMoney,
  formatInputMoneyWithDecimal,
  formatInputNumberOnly,
  formatPriceWithoutCurrency,
  formatInputByCountry,
  parseByCountry
} from "@/app/infrastructures/misc/Utils";
import { configPrice } from "@/app/infrastructures/misc/common-library/modules/Price";
import { parseZodError } from "../../../create-booking-v3/utils";
import {
  goodsPriceSchema,
  itemNameSchema,
  quantitySchema
} from "./item-details-validation";
import ModalAllCommodityV4 from "./modal-all-commodity.vue";
import { SttAdjustmentRule } from "@/domain/entities/ShipmentBooking";
import { ConfigValidationForm } from "@/domain/entities/CreateBookingV3";
import { AccountController } from "@/app/ui/controllers/AccountController";

type SupportedCurrency = "IDR" | "MYR";
type RowField = "name" | "quantity" | "goodsPrice";

const parseNumeric = (value: string): number => {
  const normalized = String(value || "")
    .replace(/\./g, "")
    .replace(",", ".");
  return parseFloat(normalized) || 0;
};

const parseQuantity = (value: string): number => {
  return Number(String(value || "").replace(/\D/g, "")) || 0;
};

class Props {
  modelValue = prop<CommodityCiplBookingV4[]>({
    required: true,
    default: () => []
  });
  errors = prop<ErrorBookingV4>({
    required: true,
    default: () => new ErrorBookingV4(),
    type: Object
  });
  isFtz = prop<boolean>({ required: true, type: Boolean });
  isForeign = prop<boolean>({ default: false, type: Boolean });
  isRequired = prop<boolean>({ default: true, type: Boolean });
  localCurrency = prop<SupportedCurrency>({ default: "IDR" });
  currencyPrefix = prop<"Rp" | "RM">({ required: true });
  numericLocale = prop<"id" | "my">({ default: "id" });
  estimatedConversion = prop<{ currency: string; amount: number } | null>({
    default: null
  });
  deminimisThreshold = prop<number | null>({ default: null });
  deminimisExceeded = prop<boolean>({ default: false, type: Boolean });
  destinationCurrencyCode = prop<string>({ default: "" });
  recipientImportDocRequired = prop<boolean>({ default: false, type: Boolean });
  selfClearanceRequired = prop<boolean>({ default: false, type: Boolean });
  maxRows = prop<number>({ default: 15, type: Number });
  commodities = prop<CommodityBookingV4[]>({ default: () => [] });
  // Adjust v4 per-field rule (GENESIS-20182) — optional, default EDITABLE
  configGoodsPrice = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  // CIPL Detail Barang section rule (GENESIS-20184) — interCipl / ftzCipl
  configCipl = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  isReverseJourney = prop<boolean>({ default: false, type: Boolean });
}

@Options({
  name: "ItemDetailsV4",
  components: { ModalAllCommodityV4, SelectSearch },
  emits: ["update:modelValue", "validationChange"],
  watch: {
    modelValue: {
      deep: true,
      handler(newVal: CommodityCiplBookingV4[]) {
        if (!newVal.length) {
          this.emitRows([
            new CommodityCiplBookingV4({
              id: 1,
              quantity: "0",
              goodsPrice: "0"
            })
          ]);
          return;
        }
        this.emitValidationChange(this.modelValue);
      }
    },
    commodities: {
      deep: true,
      handler() {
        if (this.commodities.length) {
          this.commodityOptions = this.localizeCommodities(this.commodities);
        }
      }
    },
    isRequired() {
      this.emitValidationChange(this.modelValue);
    }
  }
})
export default class ItemDetailsV4 extends Vue.with(Props) {
  touchedFields: Set<string> = new Set();
  commodityOptions: CommodityBookingV4[] = [];
  recommendedCommodities: CommodityBookingV4[] = [];
  modalSearchResults: CommodityBookingV4[] = [];
  commodityCategories: CommodityCategoryV4[] = [];
  categoryCommodities: CommodityBookingV4[] = [];
  selectedCategoryCode: string | null = null;
  activeRowId = 0;
  commodityPage = 1;
  commodityHasMore = true;
  currentSearch = "";
  loadingSearch = false;
  loadingModal = false;
  loadingCategories = false;
  showCommodityModal = false;

  mounted() {
    this.commodityOptions = this.localizeCommodities(this.commodities);
    if (!this.modelValue.length) {
      this.emitRows([
        new CommodityCiplBookingV4({ id: 1, quantity: "0", goodsPrice: "0" })
      ]);
      return;
    }
    this.emitValidationChange(this.modelValue);
  }

  get fieldsAreRequired(): boolean {
    return (this.isFtz && !this.isReverseJourney) || this.isRequired;
  }

  get disabledCipl(): boolean {
    return this.configCipl.rule.includes(ConfigValidationForm.NON_EDITABLE);
  }

  get hiddenCipl(): boolean {
    return this.configCipl.rule.includes(ConfigValidationForm.NON_SHOW);
  }

  mapCommodity(item: Commodity): CommodityBookingV4 {
    const localizedName = this.isForeign
      ? item.commodity_name_en || item.commodity_name
      : item.commodity_name;
    return new CommodityBookingV4({
      id: item.commodity_id,
      name: localizedName,
      nameEn: item.commodity_name_en,
      code: item.commodity_code,
      hsCode: item.hs_code,
      status: item.commodity_status,
      isDangerousGoods: item.isDangerousGoods,
      isQuarantine: item.isQuarantine
    });
  }

  emitRows(rows: CommodityCiplBookingV4[]) {
    this.$emit("update:modelValue", rows);
  }

  emitValidationChange(rows: CommodityCiplBookingV4[]) {
    this.$emit(
      "validationChange",
      !this.fieldsAreRequired ||
        (rows.length > 0 &&
          rows.every((_, index) => this.isRowValid(index, rows)))
    );
  }

  updateRow(index: number, fields: Partial<CommodityCiplBookingV4>) {
    if (this.disabledCipl) return;
    const rows = this.modelValue.map((item, itemIndex) =>
      itemIndex === index
        ? new CommodityCiplBookingV4({ ...item, ...fields })
        : item
    );
    this.emitRows(rows);
  }

  isRowValid(index: number, rows = this.modelValue): boolean {
    const item = rows[index];
    return (
      !!item &&
      !parseZodError(itemNameSchema, item.name) &&
      !parseZodError(quantitySchema, item.quantity) &&
      !parseZodError(goodsPriceSchema, item.goodsPrice)
    );
  }

  rowError(itemId: number, field: RowField): string {
    if (!this.fieldsAreRequired || !this.isFieldTouched(itemId, field)) {
      return "";
    }
    const item = this.modelValue.find((row) => row.id === itemId);
    if (!item) return "";
    if (field === "name") return parseZodError(itemNameSchema, item.name);
    if (field === "quantity") {
      return parseZodError(quantitySchema, item.quantity);
    }
    return parseZodError(goodsPriceSchema, item.goodsPrice);
  }

  translatedRowError(itemId: number, field: RowField): string {
    const errorKey = this.rowError(itemId, field);
    if (!errorKey) return "";
    return this.$t(errorKey, { currency: this.currencyPrefix }) as string;
  }

  fieldTouchKey(itemId: number, field: RowField): string {
    return `${itemId}:${field}`;
  }

  isFieldTouched(itemId: number, field: RowField): boolean {
    return this.touchedFields.has(this.fieldTouchKey(itemId, field));
  }

  touchField(itemId: number, field: RowField) {
    this.touchedFields = new Set(this.touchedFields).add(
      this.fieldTouchKey(itemId, field)
    );
  }

  touchAllRows() {
    const allTouched = new Set<string>();

    this.modelValue.forEach((row) => {
      allTouched.add(this.fieldTouchKey(row.id, "name"));
      allTouched.add(this.fieldTouchKey(row.id, "quantity"));
      allTouched.add(this.fieldTouchKey(row.id, "goodsPrice"));
    });

    this.touchedFields = allTouched;
  }

  commodityModel(
    item: CommodityCiplBookingV4
  ): CommodityCiplBookingV4 | string {
    return item.name ? item : "";
  }

  handleInputCommodity(searchValue = "", index = 0) {
    if (searchValue) {
      this.updateRow(index, {
        name: searchValue,
        commodityId: 0,
        code: searchValue,
        hsCode: "",
        status: "",
        isDangerousGoods: false,
        isQuarantine: false
      });
    }
    this.commodityPage = 1;
    this.commodityHasMore = true;
    this.fetchCommoditySearch(searchValue);
  }

  onCommodityFocus(index: number) {
    this.activeRowId = this.modelValue[index].id;
    this.commodityPage = 1;
    this.commodityHasMore = true;
    if (!this.modelValue[index].commodityId) this.fetchCommoditySearch("");
  }

  fetchCommoditySearch = debounce(async (keyword: string) => {
    this.currentSearch = keyword;
    this.loadingSearch = true;
    try {
      const result = await this.getCommodityList({
        search: keyword,
        page: 1,
        limit: 20
      });
      if (this.currentSearch === keyword) {
        this.commodityOptions = result;
        this.commodityHasMore = result.length === 20;
      }
    } catch {
      if (this.currentSearch === keyword) {
        this.commodityOptions = [];
        this.commodityHasMore = false;
      }
    } finally {
      if (this.currentSearch === keyword) this.loadingSearch = false;
    }
  }, 250);

  async fetchCommodityListInfiniteScroll(search = "") {
    if (this.loadingSearch || !this.commodityHasMore) return;
    this.loadingSearch = true;
    const nextPage = this.commodityPage + 1;
    try {
      const result = await this.getCommodityList({
        search,
        page: nextPage,
        limit: 20
      });
      const existingCodes = new Set(
        this.commodityOptions.map((commodity) => commodity.code)
      );
      this.commodityOptions = [
        ...this.commodityOptions,
        ...result.filter((commodity) => !existingCodes.has(commodity.code))
      ];
      this.commodityPage = nextPage;
      this.commodityHasMore = result.length === 20;
    } catch {
      this.commodityHasMore = false;
    } finally {
      this.loadingSearch = false;
    }
  }

  async getCommodityList(fields: Partial<RequestListCommodityV4>) {
    const result = await BookingV4Controller.getCommodityList(
      new RequestListCommodityV4({
        page: 1,
        limit: 20,
        commodityStatus: "active",
        cache: false,
        isDisplay: true,
        countryCode: this.accountCountryCode.toUpperCase(),
        ...fields
      })
    );
    return result.data.map((item: Commodity) => this.mapCommodity(item));
  }

  selectCommodity(index: number, commodity: CommodityBookingV4) {
    this.updateRow(index, {
      name: commodity.name,
      commodityId: commodity.id,
      code: commodity.code,
      hsCode: commodity.hsCode,
      status: commodity.status,
      isDangerousGoods: commodity.isDangerousGoods,
      isQuarantine: commodity.isQuarantine
    });
  }

  onCommodityModelUpdate(value: CommodityBookingV4 | string, index: number) {
    if (typeof value === "object" && value) {
      this.selectCommodity(index, value);
      return;
    }
    if (!value) {
      this.updateRow(index, {
        name: "",
        commodityId: 0,
        code: "",
        hsCode: "",
        status: "",
        isDangerousGoods: false,
        isQuarantine: false
      });
    }
  }

  formatQuantity(value: string) {
    return formatInputByCountry(String(value), {
      type: "quantity",
      country: this.numericLocale
    });
  }

  get accountCountryCode(): string {
    return AccountController.accountData.countryCode;
  }

  formatGoodsPrice(value: string) {
    return formatInputByCountry(value, {
      type: "money",
      country: this.numericLocale
    });
  }

  updateQuantity(index: number, value: string) {
    this.updateRow(index, { quantity: this.formatQuantity(value) });
  }

  updateGoodsPrice(index: number, value: string) {
    this.updateRow(index, { goodsPrice: this.formatGoodsPrice(value) });
  }

  addRow() {
    if (this.disabledCipl) return;
    if (this.modelValue.length >= this.maxRows) return;
    const nextId =
      this.modelValue.reduce((max, item) => Math.max(max, item.id), 0) + 1;
    this.emitRows([
      ...this.modelValue,
      new CommodityCiplBookingV4({ id: nextId, quantity: "0", goodsPrice: "0" })
    ]);
  }

  removeRow(index: number) {
    if (this.disabledCipl) return;
    if (this.modelValue.length <= 1) return;
    const removedId = this.modelValue[index].id;
    const removedRowPrefix = `${removedId}:`;
    this.touchedFields = new Set(
      [...this.touchedFields].filter(
        (fieldKey) => !fieldKey.startsWith(removedRowPrefix)
      )
    );
    this.emitRows(
      this.modelValue.filter((_, itemIndex) => itemIndex !== index)
    );
  }

  async openCommodityModal(index: number) {
    if (this.disabledCipl) return;
    this.activeRowId = this.modelValue[index].id;
    this.showCommodityModal = true;
    this.modalSearchResults = [];
    this.categoryCommodities = [];
    this.selectedCategoryCode = null;
    this.loadingModal = true;
    this.loadingCategories = true;
    try {
      const [recommended, categories] = await Promise.all([
        this.getCommodityList({ isRecommendedOnly: true, limit: 6 }),
        CommodityController.getCommodityCategory(
          this.accountCountryCode.toUpperCase()
        )
      ]);
      this.recommendedCommodities = recommended;
      this.commodityCategories = categories.map(
        (item: CommodityCategoryData) => ({
          code: item.commodityCategoryCode,
          name: item.commodityCategoryNameId,
          nameEn: item.commodityCategoryNameEn
        })
      );
    } catch {
      this.recommendedCommodities = [];
      this.commodityCategories = [];
    } finally {
      this.loadingModal = false;
      this.loadingCategories = false;
    }
  }

  async searchModalCommodities(keyword: string) {
    this.loadingModal = true;
    try {
      this.modalSearchResults = keyword
        ? await this.getCommodityList({ search: keyword, limit: 20 })
        : [];
    } catch {
      this.modalSearchResults = [];
    } finally {
      this.loadingModal = false;
    }
  }

  async selectCommodityCategory(code: string) {
    this.selectedCategoryCode = code;
    this.loadingModal = true;
    try {
      this.categoryCommodities = await this.getCommodityList({
        commodityCategoryCode: code,
        limit: 100
      });
    } catch {
      this.categoryCommodities = [];
    } finally {
      this.loadingModal = false;
    }
  }

  selectModalCommodity(commodity: CommodityBookingV4) {
    const index = this.modelValue.findIndex(
      (item) => item.id === this.activeRowId
    );
    if (index >= 0) this.selectCommodity(index, commodity);
    this.showCommodityModal = false;
  }

  rowTotal(item: CommodityCiplBookingV4): number {
    const goodPrice = parseByCountry(item.goodsPrice, {
      country: this.accountCountryCode.toUpperCase()
    });
    return parseQuantity(item.quantity) * goodPrice;
  }

  formatRowTotal(item: CommodityCiplBookingV4): string {
    return `${this.currencyPrefix}${formatInputByCountry(this.rowTotal(item), {
      type: "money",
      country: this.numericLocale
    })}`;
  }

  localizeCommodities(list: CommodityBookingV4[]): CommodityBookingV4[] {
    return list.map(
      (item) =>
        new CommodityBookingV4({
          ...item,
          name: this.isForeign ? item.nameEn || item.name : item.name
        })
    );
  }

  resetTouchedFields() {
    this.touchedFields = new Set();
  }

  get footerTotal(): number {
    return this.modelValue.reduce((sum, item) => sum + this.rowTotal(item), 0);
  }

  get footerTotalFormatted(): string {
    const separator = this.currencyPrefix === "Rp" ? "" : " ";
    return `${this.currencyPrefix}${separator}${formatPriceWithoutCurrency(
      this.footerTotal,
      this.localCurrency
    )}`;
  }

  get footerEstimatedFormatted(): string {
    if (!this.estimatedConversion) return "";
    return this.$t("bookingV4.goods.cipl.footerEstimated", {
      currency: this.estimatedCurrencyPrefix,
      amount: this.estimatedConversion.amount.toLocaleString(
        configPrice[this.estimatedConversion.currency]?.locales,
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }
      )
    }) as string;
  }

  get estimatedCurrencyPrefix(): string {
    return this.estimatedConversion?.currency || "";
  }

  // GENESIS-20471 — trusts the live resolve() result's deminimis_exceeded
  // flag (BE already picked the winning row across specific/ALL commodities
  // and converted currency) instead of re-deriving "amount > threshold"
  // client-side, which would only see the whole-shipment total.
  get showDeminimisInfobar(): boolean {
    return (
      this.deminimisExceeded &&
      !!this.destinationCurrencyCode &&
      this.deminimisThreshold != null
    );
  }

  get showImportDocInfobar(): boolean {
    return !this.isFtz && this.recipientImportDocRequired;
  }

  // GENESIS-20472 — independent of the other two messages; the BE
  // (GENESIS-20446) already resolves self_clearance_required per matched
  // row, so the FE just trusts the boolean, no local re-derivation.
  get showSelfClearanceInfobar(): boolean {
    return this.selfClearanceRequired;
  }

  get isEstimatedOverDeminimis(): boolean {
    if (
      !this.deminimisExceeded ||
      !this.estimatedConversion ||
      this.deminimisThreshold == null
    ) {
      return false;
    }

    const estimated =
      Math.round(Number(this.estimatedConversion.amount) * 100) / 100;

    return estimated > Number(this.deminimisThreshold);
  }

  get showInfobar(): boolean {
    return (
      this.isEstimatedOverDeminimis &&
      (this.showDeminimisInfobar ||
        this.showImportDocInfobar ||
        this.showSelfClearanceInfobar)
    );
  }

  get deminimisThresholdFormatted(): string {
    if (!this.destinationCurrencyCode || this.deminimisThreshold == null)
      return "";

    const formatCurrency =
      this.destinationCurrencyCode === "MYR" ? "MYR" : "IDR";

    const amount = formatPriceWithoutCurrency(
      this.deminimisThreshold,
      formatCurrency as "IDR" | "MYR"
    );

    return `${this.destinationCurrencyCode} ${amount}`;
  }

  get deminimisMessagePrefix(): string {
    return (this.$t("bookingV4.goods.cipl.deminimisWarning", {
      currency: "",
      amount: ""
    }) as string)
      .replace(/\s*\.\s*$/, "")
      .trim();
  }
}
